import React, { useCallback, useEffect, useState } from 'react';
import { Collapse, message, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import { F } from '@/utils/intl';
import {
  fetchMailboxUserStatusesRequest,
  fetchMonitoredMailboxesRequest,
  setMailboxUserOverrideRequest,
} from '@/services/api/email';
import { MailboxUserStatus } from '@/typings/models/Email';
import { usePartnerIntegrations } from '@/utils/hooks';
import { processAPIResponseNow } from '@/utils/request';

const MAILBOX_OPTION_LIMIT = 50;

/**
 * Whether this panel will render anything, for callers that need to know
 * before it does — the generic Integrations list shows an empty state, and
 * "no integrations" is wrong when a Microsoft 365 mailbox panel is sitting
 * right underneath it.
 */
export const useHasOffice365MailUserPanel = (userUuid?: string): boolean => {
  const partnerIntegrations = usePartnerIntegrations();

  return Boolean(
    userUuid &&
      (partnerIntegrations || []).some(
        (integration: any) =>
          integration.integration_type === 'office365_mail' ||
          integration.type === 'office365_mail',
      ),
  );
};

interface Office365MailUserPanelProps {
  userUuid?: string;
  userEmail?: string;
  disabled?: boolean;
}

/**
 * This user's Microsoft 365 mailbox inside the user drawer: whether they are
 * connected and how (directory sync / email match / manual assignment), plus
 * the assignment control itself.
 *
 * Not part of the drawer's `integrations` form field on purpose — that field
 * only carries {integration_type, integration_id} pairs and is saved by a
 * full delete-and-recreate, which cannot express (or preserve) a mailbox
 * assignment. This panel writes through the dedicated mailbox-override
 * endpoint instead.
 */
const Office365MailUserPanel: React.FC<Office365MailUserPanelProps> = ({
  userUuid,
  userEmail,
  disabled = false,
}) => {
  const intl = useIntl();
  const partnerIntegrations = usePartnerIntegrations();
  // `/me` returns these as `integration_type`; other payloads use `type`. Both
  // shapes are in play, so match either — see CalendarIntegrationConnect.
  const installed = (partnerIntegrations || []).some(
    (integration: any) =>
      integration.integration_type === 'office365_mail' || integration.type === 'office365_mail',
  );

  const [status, setStatus] = useState<MailboxUserStatus | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<string[]>([]);

  const load = useCallback(() => {
    if (!userEmail) return;
    // Reuses the paginated status list filtered to this one user rather than
    // adding a per-user endpoint; the search is an exact email so it returns
    // at most one row.
    fetchMailboxUserStatusesRequest({ search: userEmail, per_page: 5 }).then(
      (Response: any) => {
        processAPIResponseNow(
          Response,
          {
            onSuccess(response: any) {
              const match = (response.items || []).find(
                (item: MailboxUserStatus) =>
                  item.email?.toLowerCase() === userEmail.toLowerCase(),
              );
              setStatus(match || null);
            },
            onEnd() {
              setLoaded(true);
            },
          },
          'data',
        );
      },
    );
  }, [userEmail]);

  useEffect(() => {
    if (installed) load();
  }, [installed, load]);

  // Server-side filtered: the directory is one entry per mail-enabled tenant
  // user, so it is never loaded whole into this dropdown.
  const searchMailboxes = useCallback((search?: string) => {
    fetchMonitoredMailboxesRequest({ search, limit: MAILBOX_OPTION_LIMIT }).then(
      (Response: any) => {
        processAPIResponseNow(
          Response,
          {
            onSuccess(response: any) {
              setOptions((response.mailboxes || []).map((m: any) => m.address));
            },
          },
          'data',
        );
      },
    );
  }, []);

  useEffect(() => {
    if (installed) searchMailboxes();
  }, [installed, searchMailboxes]);

  if (!installed || !userUuid) return null;

  // The resolved mailbox IS their login email, so the directory match maintains
  // it and there is no decision left to make. Compared on the address rather
  // than on resolved_source, because both a live email match and a stored
  // directory-sync row can be identical matches.
  const autoMatched =
    !!status?.resolved_mailbox &&
    !!userEmail &&
    status.resolved_mailbox.toLowerCase() === userEmail.toLowerCase();

  const handleChange = (address: string | null) => {
    setSaving(true);
    setMailboxUserOverrideRequest(userUuid, address).then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess() {
            load();
          },
          onError(error: any) {
            message.error(error?.message);
            // Same as the integrations table: refetch so a rejected assignment
            // doesn't leave the Select displaying a mailbox that wasn't saved.
            load();
          },
          onEnd() {
            setSaving(false);
          },
        },
        'data',
      );
    });
  };

  const sourceLabel = {
    manual_override: intl.formatMessage({
      id: 'pages.integrations.office365_mail.source_override',
      defaultMessage: 'Manual override',
    }),
    directory_sync: intl.formatMessage({
      id: 'pages.integrations.office365_mail.source_directory',
      defaultMessage: 'Directory sync',
    }),
    email_match: intl.formatMessage({
      id: 'pages.integrations.office365_mail.source_email_match',
      defaultMessage: 'Email match',
    }),
  }[status?.resolved_source ?? ''];

  return (
    <Collapse
      defaultActiveKey={['office365_mail']}
      items={[
        {
          key: 'office365_mail',
          label: (
            <Space>
              <F
                id="pages.users.office365_mail.title"
                defaultMessage="Microsoft 365"
              />
              {loaded &&
                (status?.resolved_mailbox ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    <F
                      id="pages.users.office365_mail.connected"
                      defaultMessage="Connected"
                    />
                  </Tag>
                ) : (
                  <Tag color="warning" icon={<InfoCircleOutlined />}>
                    <F
                      id="pages.integrations.office365_mail.not_connected_tag"
                      defaultMessage="Not connected"
                    />
                  </Tag>
                ))}
            </Space>
          ),
          children: !loaded ? (
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          ) : (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Typography.Text type="secondary">
                {status?.resolved_mailbox ? (
                  autoMatched ? (
                    <F
                      id="pages.users.office365_mail.resolved_automatic"
                      defaultMessage="Sends and reads as {mailbox}, matched automatically to their email address."
                      values={{ mailbox: status.resolved_mailbox }}
                    />
                  ) : (
                    <F
                      id="pages.users.office365_mail.resolved"
                      defaultMessage="Sends and reads as {mailbox} ({source})"
                      values={{ mailbox: status.resolved_mailbox, source: sourceLabel }}
                    />
                  )
                ) : (
                  <F
                    id="pages.users.office365_mail.unresolved"
                    defaultMessage="No Microsoft 365 mailbox is linked to this user, so they cannot read or send lead email. Pick one below."
                  />
                )}
              </Typography.Text>
              {/* Same rule as the settings table: when the mailbox IS their
                  login email there is nothing to choose, and picking it by hand
                  would only turn a self-maintaining match into a manual
                  assignment that stops following the directory. */}
              {!autoMatched && (
              <Select
                showSearch
                allowClear
                // Server already filtered; filtering again locally would hide
                // results that arrived from the remote search.
                filterOption={false}
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({
                  id: 'pages.integrations.office365_mail.override_placeholder',
                  defaultMessage: 'None — use automatic resolution',
                })}
                value={status?.override ?? null}
                loading={saving}
                disabled={disabled || saving}
                onSearch={searchMailboxes}
                onChange={(address) => handleChange(address ?? null)}
                options={options.map((address) => ({ value: address, label: address }))}
              />
              )}
            </Space>
          ),
        },
      ]}
    />
  );
};

export default Office365MailUserPanel;
